import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useFamily } from "./useFamily";
import { getDateString } from "@/lib/family-date";

/**
 * Returns a Map<child_id, custodial_parent_user_id> for a given date
 * (defaults to today). Used to check if the current user has custody.
 */
export function useCustodyForDate(dateOverride?: string) {
  const { user } = useAuth();
  const { memberships, timezone } = useFamily();
  const familyIds = memberships.map((m) => m.family_id);
  const familyIdsKey = familyIds.join(",");
  const dateStr = dateOverride ?? getDateString(new Date(), timezone);
  const queryClient = useQueryClient();

  const queryKey = ["custody-for-date", familyIdsKey, dateStr];

  const { data: custodyMap = new Map<string, string>() } = useQuery({
    queryKey,
    queryFn: async () => {
      if (familyIds.length === 0) return new Map<string, string>();
      const { data, error } = await supabase
        .from("custody_days")
        .select("child_id, custodial_parent_user_id")
        .in("family_id", familyIds)
        .eq("date", dateStr);
      if (error) throw error;
      const map = new Map<string, string>();
      for (const row of data ?? []) {
        map.set(row.child_id, row.custodial_parent_user_id);
      }
      return map;
    },
    enabled: familyIds.length > 0,
  });

  // Realtime: listen for custody_days changes
  useEffect(() => {
    if (familyIds.length === 0) return;
    const channel = supabase
      .channel(`custody-rt-${familyIdsKey}-${dateStr}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "custody_days" }, () => {
        queryClient.invalidateQueries({ queryKey: ["custody-for-date"] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [familyIdsKey, dateStr, queryClient]);

  function isWithOtherParent(childId: string): boolean {
    if (!user) return false;
    const custodialParent = custodyMap.get(childId);
    if (!custodialParent) return false; // no custody record = both parents see
    return custodialParent !== user.id;
  }

  return { custodyMap, isWithOtherParent };
}
